import { Children, type FormEvent, type ReactElement, type ReactNode } from "react";

import { Button, Link, ProgressBar, Spinner } from "@shapr3d/frontend-lib";
import classNames from "classnames";

import useTranslation from "../../hooks/useTranslation";

import style from "./MultistepForm.module.css";

interface MultistepFormParams {
  children?: (ReactElement<StepParams> | false) | Array<ReactElement<StepParams> | false>;
  currentStep: number;
  goToNextCallback: () => void;
  goToPreviousCallback: () => void;
  submitCallback: (event: FormEvent) => void;
  previousDisabled: boolean;
  nextDisabled: boolean;
}

const MultiStepForm: React.FC<MultistepFormParams> = ({
  children,
  currentStep,
  submitCallback,
  goToNextCallback,
  goToPreviousCallback,
  previousDisabled,
  nextDisabled,
}) => {
  const { t, m } = useTranslation();

  const stepCount = Children.toArray(children).filter((child) => child).length;

  return (
    <form
      className={style.form}
      style={{ "--current-step": currentStep, "--step-count": stepCount } as React.CSSProperties}
      onSubmit={submitCallback}
    >
      <div className={style.stepSlider}>{children}</div>

      <div className={style.formNavigations}>
        {currentStep !== 0 && (
          <Link
            onClick={() => (previousDisabled ? undefined : goToPreviousCallback())}
            className={classNames(style.previousLink, previousDisabled && style.disabled)}
          >
            {t(m.SegmentationSurvey.General.Previous)}
          </Link>
        )}
        <ProgressBar percentage={currentStep / stepCount} />
        {currentStep !== stepCount - 1 && (
          <Button
            className={style.endButton}
            onClick={goToNextCallback}
            type="button"
            disabled={nextDisabled}
          >
            {t(m.SegmentationSurvey.General.Next)}
          </Button>
        )}
        {currentStep === stepCount - 1 && (
          <Button className={style.endButton} type="submit" disabled={nextDisabled}>
            {t(m.SegmentationSurvey.General.Submit)}
          </Button>
        )}
      </div>
    </form>
  );
};

interface StepParams {
  loading?: boolean;
}

const Step: React.FC<{ children: ReactNode } & StepParams> = ({ children, loading }) => {
  return (
    <div className={classNames(style.step)}>
      {children}
      {loading && (
        <div className={style.spinner}>
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default Object.assign(MultiStepForm, { Step });
